import {
  ComponentStateInterface,
  HealthKPIAggregatedResponseSchema,
  HealthKPIAggregationScope,
  HealthKPIAggregationType,
  HealthKPIEnum,
  HealthKPILatestParameters,
  HealthKPILatestResponseSchema,
  KpiStateDetailsInterface,
  SafetyRecommendationComponentDetailsInterface,
  SafetyRecommendationDataInterface,
  SystemIngestionStatistics,
  GetSiblingEffectResponse,
} from '@twaice-fe/shared/models';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { energyAnalyticsActions } from '../actions';

interface ErrorPayload {
  error: string;
}
export const ENERGY_ANALYTICS_FEATURE_KEY = 'energy-analytics';

export interface RemoteState {
  isLoading: boolean;
  error: string | null;
}

// region Safety Recommendation state
export interface MailNotificationSettingsState {
  isLoading: boolean;
  notificationsEnabled: boolean | undefined;
}

export interface SafetyRecommendationDataState extends RemoteState {
  systemState: string;
  numberOfStableStrings: number;
  requiredActions: EntityState<ComponentStateInterface>;
  recommendedActions: EntityState<ComponentStateInterface>;
  notificationSettings: MailNotificationSettingsState;
}

export interface SafetyRecommendationComponentDetailsState extends RemoteState {
  componentBk: string;
  kpiStates: KpiStateDetailsInterface[];
}

export const safetyActionAdapter: EntityAdapter<ComponentStateInterface> = createEntityAdapter<ComponentStateInterface>({
  selectId: (safetyActionData) => safetyActionData.componentBk,
});

export const initialSafetyRecommendationDataState: SafetyRecommendationDataState = {
  systemState: '',
  numberOfStableStrings: 0,
  requiredActions: safetyActionAdapter.getInitialState(),
  recommendedActions: safetyActionAdapter.getInitialState(),
  error: null,
  isLoading: false,
  notificationSettings: {
    isLoading: false,
    notificationsEnabled: undefined,
  },
};

export const initialSafetyRecommendationComponentDetailsState: SafetyRecommendationComponentDetailsState = {
  componentBk: '',
  kpiStates: [],
  error: null,
  isLoading: false,
};
// endregion

// region System Ingestion Statistics state
export interface SystemIngestionStatisticsState extends RemoteState {
  lastSeen: Date | string | null | undefined;
  lastRefresh: Date | null;
}

const initialSystemIngestionStatisticsState: SystemIngestionStatisticsState = {
  isLoading: false,
  error: null,
  lastSeen: undefined,
  lastRefresh: null,
};
// endregion

// region Health KPI Data State
export interface LatestHealthKPIDataState extends RemoteState {
  data: HealthKPILatestResponseSchema;
}

const initialLatestHealthKPIDataState: LatestHealthKPIDataState = {
  data: {
    customerBk: '',
    systemBk: '',
    levelBk: '',
    aggregationScope: HealthKPIAggregationScope.PER_ENTITY,
    aggregationType: HealthKPIAggregationType.NONE,
    kpi: HealthKPIEnum.EFC,
    data: [],
  },
  isLoading: false,
  error: null,
};

export interface AggregatedHealthKPIDataState extends RemoteState {
  data: HealthKPIAggregatedResponseSchema;
}

const initialAggregatedHealthKPIDataState: AggregatedHealthKPIDataState = {
  data: {
    customerBk: '',
    systemBk: '',
    levelBk: '',
    aggregationScope: HealthKPIAggregationScope.PER_ENTITY,
    aggregationType: HealthKPIAggregationType.NONE,
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    kpi: HealthKPIEnum.EFC,
    data: [],
  },
  isLoading: false,
  error: null,
};

export interface HealthKPIState {
  latest: LatestHealthKPIDataState;
  aggregated: AggregatedHealthKPIDataState;
}

// endregion

// region System Metadata state
export type HasSiblingEffectState = RemoteState & GetSiblingEffectResponse;

const initialHasSiblingEffectState: HasSiblingEffectState = {
  isLoading: false,
  error: null,
  hasSiblingEffect: false,
};
// endregion

// region State
export interface State {
  customerBk: string;
  systemBk: string;
  healthKPIData: {
    [HealthKPIEnum.DCR]: HealthKPIState;
    [HealthKPIEnum.EFC]: HealthKPIState;
    [HealthKPIEnum.SOH]: HealthKPIState;
    [HealthKPIEnum.TEMPERATURE_SPREAD]: HealthKPIState;
    [HealthKPIEnum.VOLTAGE_SPREAD]: HealthKPIState;
  };
  safetyRecommendationData: SafetyRecommendationDataState;
  systemIngestionStatistics: SystemIngestionStatisticsState;
  siblingEffect: HasSiblingEffectState;
  safetyRecommendationComponentDetails: SafetyRecommendationComponentDetailsState;
}

export const initialState: State = {
  customerBk: '',
  systemBk: '',
  healthKPIData: {
    [HealthKPIEnum.DCR]: {
      latest: { ...initialLatestHealthKPIDataState, data: { ...initialLatestHealthKPIDataState.data, kpi: HealthKPIEnum.DCR } },
      aggregated: {
        ...initialAggregatedHealthKPIDataState,
        data: { ...initialAggregatedHealthKPIDataState.data, kpi: HealthKPIEnum.DCR },
      },
    },
    [HealthKPIEnum.EFC]: {
      latest: { ...initialLatestHealthKPIDataState, data: { ...initialLatestHealthKPIDataState.data, kpi: HealthKPIEnum.EFC } },
      aggregated: {
        ...initialAggregatedHealthKPIDataState,
        data: { ...initialAggregatedHealthKPIDataState.data, kpi: HealthKPIEnum.EFC },
      },
    },
    [HealthKPIEnum.SOH]: {
      latest: { ...initialLatestHealthKPIDataState, data: { ...initialLatestHealthKPIDataState.data, kpi: HealthKPIEnum.SOH } },
      aggregated: {
        ...initialAggregatedHealthKPIDataState,
        data: { ...initialAggregatedHealthKPIDataState.data, kpi: HealthKPIEnum.SOH },
      },
    },
    [HealthKPIEnum.TEMPERATURE_SPREAD]: {
      latest: {
        ...initialLatestHealthKPIDataState,
        data: { ...initialLatestHealthKPIDataState.data, kpi: HealthKPIEnum.TEMPERATURE_SPREAD },
      },
      aggregated: {
        ...initialAggregatedHealthKPIDataState,
        data: { ...initialAggregatedHealthKPIDataState.data, kpi: HealthKPIEnum.TEMPERATURE_SPREAD },
      },
    },
    [HealthKPIEnum.VOLTAGE_SPREAD]: {
      latest: {
        ...initialLatestHealthKPIDataState,
        data: { ...initialLatestHealthKPIDataState.data, kpi: HealthKPIEnum.VOLTAGE_SPREAD },
      },
      aggregated: {
        ...initialAggregatedHealthKPIDataState,
        data: { ...initialAggregatedHealthKPIDataState.data, kpi: HealthKPIEnum.VOLTAGE_SPREAD },
      },
    },
  },
  safetyRecommendationData: { ...initialSafetyRecommendationDataState },
  systemIngestionStatistics: { ...initialSystemIngestionStatisticsState },
  siblingEffect: { ...initialHasSiblingEffectState },
  safetyRecommendationComponentDetails: { ...initialSafetyRecommendationComponentDetailsState },
};
// endregion

type ActionReducer<T = unknown> = (state: State, payload: T) => State;

//#region Safety Recommendation reducers
const reduceFetchSafetyRecommendationData: ActionReducer = (state) => ({
  ...state,
  safetyRecommendationData: { ...initialSafetyRecommendationDataState, isLoading: true },
});

const reduceLoadSafetyRecommendationDataSuccess: ActionReducer<{
  safetyRecommendationData: SafetyRecommendationDataInterface;
}> = (state, { safetyRecommendationData }) => ({
  ...state,
  safetyRecommendationData: {
    ...state.safetyRecommendationData,
    ...safetyRecommendationData,
    requiredActions: safetyActionAdapter.upsertMany(
      safetyRecommendationData.requiredActions,
      state.safetyRecommendationData.requiredActions
    ),
    recommendedActions: safetyActionAdapter.upsertMany(
      safetyRecommendationData.recommendedActions,
      state.safetyRecommendationData.recommendedActions
    ),
    isLoading: false,
    error: null,
  },
});

const reduceLoadSafetyRecommendationDataFailure: ActionReducer<ErrorPayload> = (state, { error }) => ({
  ...state,
  safetyRecommendationData: {
    ...state.safetyRecommendationData,
    isLoading: false,
    error,
  },
});

const reduceFetchSafetyRecommendationComponentDetails: ActionReducer<{ componentBk: string }> = (state, { componentBk }) => ({
  ...state,
  safetyRecommendationComponentDetails: { ...initialSafetyRecommendationComponentDetailsState, componentBk, isLoading: true },
});

const reduceLoadSafetyRecommendationComponentDetailsSuccess: ActionReducer<{
  safetyRecommendationComponentDetails: SafetyRecommendationComponentDetailsInterface;
}> = (state, { safetyRecommendationComponentDetails }) => ({
  ...state,
  safetyRecommendationComponentDetails: {
    ...state.safetyRecommendationComponentDetails,
    ...safetyRecommendationComponentDetails,
    isLoading: false,
    error: null,
  },
});

const reduceLoadSafetyRecommendationDetailsFailure: ActionReducer<ErrorPayload> = (state, { error }) => ({
  ...state,
  safetyRecommendationComponentDetails: {
    ...state.safetyRecommendationComponentDetails,
    isLoading: false,
    error,
  },
});

const reduceFetchSafetyMailNotificationConfig: ActionReducer = (state) => ({
  ...state,
  safetyRecommendationData: {
    ...state.safetyRecommendationData,
    notificationSettings: {
      ...state.safetyRecommendationData.notificationSettings,
      isLoading: true,
    },
  },
});

const reduceSafetyMailNotificationConfigSuccess: ActionReducer<{ notificationsEnabled: boolean }> = (
  state,
  { notificationsEnabled }
) => ({
  ...state,
  safetyRecommendationData: {
    ...state.safetyRecommendationData,
    notificationSettings: {
      isLoading: false,
      notificationsEnabled: notificationsEnabled,
    },
  },
});

const reduceSafetyMailNotificationConfigFailure: ActionReducer<ErrorPayload> = (state, { error }) => ({
  ...state,
  safetyRecommendationData: {
    ...state.safetyRecommendationData,
    notificationSettings: {
      ...state.safetyRecommendationData.notificationSettings,
      isLoading: false,
    },
  },
});
// endregion

// region  System Ingestion Statistics reducers
const reduceFetchSystemIngestionStatisticsData: ActionReducer = (state) => ({
  ...state,
  systemIngestionStatistics: { ...initialSystemIngestionStatisticsState, isLoading: true },
});

const reduceLoadSystemIngestionStatisticsDataSuccess: ActionReducer<{ systemIngestionStatistics: SystemIngestionStatistics }> = (
  state,
  { systemIngestionStatistics }
) => ({
  ...state,
  systemIngestionStatistics: {
    ...state.systemIngestionStatistics,
    ...systemIngestionStatistics,
    isLoading: false,
    error: null,
    lastRefresh: new Date(),
  },
});

const reduceLoadSystemIngestionStatisticsDataFailure: ActionReducer<ErrorPayload> = (state, { error }) => ({
  ...state,
  systemIngestionStatistics: {
    ...state.systemIngestionStatistics,
    isLoading: false,
    error,
  },
});
// endregion

// region  System Ingestion Statistics reducers
const reduceFetchHasSiblingEffect: ActionReducer = (state) => ({
  ...state,
  siblingEffect: { ...initialHasSiblingEffectState, isLoading: true },
});

const reduceLoadHasSiblingEffectSuccess: ActionReducer<{ hasSiblingEffect: GetSiblingEffectResponse }> = (
  state,
  { hasSiblingEffect }
) => ({
  ...state,
  siblingEffect: {
    ...state.siblingEffect,
    ...hasSiblingEffect,
    isLoading: false,
    error: null,
  },
});

const reduceLoadHasSiblingEffectFailure: ActionReducer<ErrorPayload> = (state, { error }) => ({
  ...state,
  hasSiblingEffect: {
    ...state.siblingEffect,
    isLoading: false,
    error,
  },
});
// endregion

// region Health KPI reducers
const reduceFetchLatestHealthKPIData: ActionReducer<{ kpi: HealthKPIEnum; params: HealthKPILatestParameters }> = (
  state,
  { kpi }
) => ({
  ...state,
  healthKPIData: {
    ...state.healthKPIData,
    [kpi]: {
      latest: { ...initialLatestHealthKPIDataState, isLoading: true, error: null },
      aggregated: state.healthKPIData[kpi].aggregated,
    },
  },
});

const reduceLoadLatestHealthKPIDataSuccess: ActionReducer<HealthKPILatestResponseSchema> = (state, healthKPIData) => ({
  ...state,
  healthKPIData: {
    ...state.healthKPIData,
    [healthKPIData.kpi]: {
      latest: {
        data: {
          ...state.healthKPIData[healthKPIData.kpi].latest.data,
          ...healthKPIData,
        },
        isLoading: false,
        error: null,
      },
      aggregated: state.healthKPIData[healthKPIData.kpi].aggregated,
    },
  },
});

const reduceLoadLatestHealthKPIDataFailure: ActionReducer<{ kpi: HealthKPIEnum; error: string }> = (state, { kpi, error }) => ({
  ...state,
  healthKPIData: {
    ...state.healthKPIData,
    [kpi]: {
      latest: { ...state.healthKPIData[kpi].latest, isLoading: false, error },
      aggregated: state.healthKPIData[kpi].aggregated,
    },
  },
});

const reduceFetchAggregatedHealthKPIData: ActionReducer<{ kpi: HealthKPIEnum; params: HealthKPILatestParameters }> = (
  state,
  { kpi }
) => ({
  ...state,
  healthKPIData: {
    ...state.healthKPIData,
    [kpi]: {
      latest: state.healthKPIData[kpi].latest,
      aggregated: { ...initialAggregatedHealthKPIDataState, isLoading: true, error: null },
    },
  },
});

const reduceLoadAggregatedHealthKPIDataSuccess: ActionReducer<HealthKPIAggregatedResponseSchema> = (state, healthKPIData) => ({
  ...state,
  healthKPIData: {
    ...state.healthKPIData,
    [healthKPIData.kpi]: {
      latest: state.healthKPIData[healthKPIData.kpi].latest,
      aggregated: {
        data: {
          ...state.healthKPIData[healthKPIData.kpi].aggregated.data,
          ...healthKPIData,
        },
        isLoading: false,
        error: null,
      },
    },
  },
});

const reduceLoadAggregatedHealthKPIDataFailure: ActionReducer<{ kpi: HealthKPIEnum; error: string }> = (
  state,
  { kpi, error }
) => ({
  ...state,
  healthKPIData: {
    ...state.healthKPIData,
    [kpi]: {
      latest: state.healthKPIData[kpi].latest,
      aggregated: { ...state.healthKPIData[kpi].aggregated, isLoading: false, error },
    },
  },
});
// endregion

export const energyAnalyticsReducer = createReducer(
  initialState,
  on(energyAnalyticsActions.fetchSafetyRecommendationData, reduceFetchSafetyRecommendationData),
  on(energyAnalyticsActions.loadSafetyRecommendationDataSuccess, reduceLoadSafetyRecommendationDataSuccess),
  on(energyAnalyticsActions.loadSafetyRecommendationDataFailure, reduceLoadSafetyRecommendationDataFailure),
  on(energyAnalyticsActions.fetchSafetyRecommendationComponentDetails, reduceFetchSafetyRecommendationComponentDetails),
  on(
    energyAnalyticsActions.loadSafetyRecommendationComponentDetailsSuccess,
    reduceLoadSafetyRecommendationComponentDetailsSuccess
  ),
  on(energyAnalyticsActions.loadSafetyRecommendationComponentDetailsFailure, reduceLoadSafetyRecommendationDetailsFailure),
  on(energyAnalyticsActions.fetchSafetyMailNotificationConfig, reduceFetchSafetyMailNotificationConfig),
  on(energyAnalyticsActions.updateSafetyMailNotificationConfig, reduceFetchSafetyMailNotificationConfig),
  on(energyAnalyticsActions.fetchSafetyMailNotificationConfigSuccess, reduceSafetyMailNotificationConfigSuccess),
  on(energyAnalyticsActions.updateSafetyMailNotificationConfigSuccess, reduceSafetyMailNotificationConfigSuccess),
  on(energyAnalyticsActions.fetchSafetyMailNotificationConfigFailure, reduceSafetyMailNotificationConfigFailure),
  on(energyAnalyticsActions.updateSafetyMailNotificationConfigFailure, reduceSafetyMailNotificationConfigFailure),
  on(energyAnalyticsActions.fetchSystemIngestionStatisticsData, reduceFetchSystemIngestionStatisticsData),
  on(energyAnalyticsActions.loadSystemIngestionStatisticsSuccess, reduceLoadSystemIngestionStatisticsDataSuccess),
  on(energyAnalyticsActions.loadSystemIngestionStatisticsFailure, reduceLoadSystemIngestionStatisticsDataFailure),
  on(energyAnalyticsActions.fetchHasSiblingEffect, reduceFetchHasSiblingEffect),
  on(energyAnalyticsActions.loadHasSiblingEffectSuccess, reduceLoadHasSiblingEffectSuccess),
  on(energyAnalyticsActions.loadHasSiblingEffectFailure, reduceLoadHasSiblingEffectFailure),
  on(energyAnalyticsActions.fetchLatestHealthKPIData, reduceFetchLatestHealthKPIData),
  on(energyAnalyticsActions.loadLatestHealthKPIDataSuccess, reduceLoadLatestHealthKPIDataSuccess),
  on(energyAnalyticsActions.loadLatestHealthKPIDataFailure, reduceLoadLatestHealthKPIDataFailure),
  on(energyAnalyticsActions.fetchAggregatedHealthKPIData, reduceFetchAggregatedHealthKPIData),
  on(energyAnalyticsActions.loadAggregatedHealthKPIDataSuccess, reduceLoadAggregatedHealthKPIDataSuccess),
  on(energyAnalyticsActions.loadAggregatedHealthKPIDataFailure, reduceLoadAggregatedHealthKPIDataFailure)
);
